import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadCompanyProfile } from '../../utils/companyProfileCache';
import { getValidAuthToken } from '../../utils/api/auth';
import { isCompanyStaff, isCompanyUser } from '../../utils/roleUtils';
import { useCompanyAccess } from '../../context/CompanyAccessContext';
import {
  getPlanRenewalDaysRemaining,
  PLAN_RENEWAL_REMINDER_DISMISSED_KEY,
  shouldShowPlanRenewalReminder,
} from '../../utils/subscriptionRenewal';
import PlanRenewalReminderModal from './PlanRenewalReminderModal';

const LOGIN_SHOW_DELAY_MS = 800;

/**
 * Shows a one-time-per-session renewal reminder after login when paid plan expires within 7 days.
 */
export default function PlanRenewalGate({ children }) {
  const { isBillingOnly, loading: accessLoading } = useCompanyAccess();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const companyUser = isCompanyUser();
  const canUpgradePlan = !isCompanyStaff();

  const daysRemaining = getPlanRenewalDaysRemaining(profile);
  const showReminder = shouldShowPlanRenewalReminder(daysRemaining);

  const loadProfile = useCallback(async () => {
    if (!companyUser || !getValidAuthToken() || isBillingOnly) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    setProfileLoading(true);
    try {
      const data = await loadCompanyProfile();
      setProfile(data || null);
    } catch {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, [companyUser, isBillingOnly]);

  useEffect(() => {
    if (accessLoading) return;
    loadProfile();
  }, [accessLoading, loadProfile]);

  useEffect(() => {
    if (accessLoading || profileLoading || !companyUser || isBillingOnly || !showReminder) {
      return undefined;
    }

    if (sessionStorage.getItem(PLAN_RENEWAL_REMINDER_DISMISSED_KEY) === '1') {
      return undefined;
    }

    const timer = setTimeout(() => {
      setModalOpen(true);
    }, LOGIN_SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [accessLoading, profileLoading, companyUser, isBillingOnly, showReminder]);

  const handleClose = () => {
    setModalOpen(false);
    sessionStorage.setItem(PLAN_RENEWAL_REMINDER_DISMISSED_KEY, '1');
  };

  const handleUpgrade = () => {
    setModalOpen(false);
    sessionStorage.setItem(PLAN_RENEWAL_REMINDER_DISMISSED_KEY, '1');
    navigate('/billing?tab=subscription');
  };

  return (
    <>
      {children}
      <PlanRenewalReminderModal
        isOpen={modalOpen}
        daysRemaining={daysRemaining || 0}
        planName={profile?.plan || ''}
        canUpgradePlan={canUpgradePlan}
        onClose={handleClose}
        onUpgrade={handleUpgrade}
      />
    </>
  );
}
